import { Form, Input, InputNumber, Button, Modal } from 'antd'
import classNames from 'classnames'
import FormSection from 'components/form-section'
import React, { useEffect, useState, useRef } from 'react'
import { TableOutlined } from '@ant-design/icons'
import TableEditor from './TableEditor'
import { generateUuid } from 'utils/dataUtil'

const units = {
  stageLength: {
    FIELD: '(ft)',
    METRIC: '(m)'
  },
  sandUsage: {
    FIELD: '(lb)',
    METRIC: '(kg)'
  },
  fractureHalfLength: {
    FIELD: '(ft)',
    METRIC: '(m)'
  },
  verticalPenetration: {
    FIELD: '(ft)',
    METRIC: '(m)'
  },
  fractureWidth: {
    FIELD: '(in)',
    METRIC: '(cm)'
  },
}

const GenericSettings = ({ stageIndex, isActive }) => {
  const form = Form.useFormInstance()
  const currentStageValues = Form.useWatch(['Completion', 'hydraulic_fracture', stageIndex])
  const simulationUnit = Form.useWatch(["GENERAL", "unit"])
  const [isTableEditorOpen, setIsTableEditorOpen] = useState(false)
  const [editingData, setEditingData] = useState([])
  const tableEditorRef = useRef(null)

  const tableColumns = [
    {
      title: "Stage Length " + (units.stageLength[simulationUnit] ?? ''),
      dataIndex: "STAGE_LEN",
      key: "STAGE_LEN",
      editable: true
    },
    {
      title: "Number of Clusters",
      dataIndex: "NUMBER_CLUSTERS",
      key: "NUMBER_CLUSTERS",
      editable: true
    },
    {
      title: "Sand Usage " + (units.sandUsage[simulationUnit] ?? ''),
      dataIndex: "SAND_USAGE",
      key: "SAND_USAGE",
      editable: true
    },
    {
      title: "Primary Fracture Half Length " + (units.fractureHalfLength[simulationUnit] ?? ''),
      dataIndex: "PRIMARY_FRACTURE_HALF_LEN",
      key: "PRIMARY_FRACTURE_HALF_LEN",
      editable: true
    },
    {
      title: "Primary Vertical Penetration " + (units.verticalPenetration[simulationUnit] ?? ''),
      dataIndex: "PRIMARY_VERTICAL_PENETRATION",
      key: "PRIMARY_VERTICAL_PENETRATION",
      editable: true
    },
    {
      title: "Primary Fracture Width " + (units.fractureWidth[simulationUnit] ?? ''),
      dataIndex: "PRIMARY_FRACTURE_WIDTH",
      key: "PRIMARY_FRACTURE_WIDTH",
      editable: true
    },
    {
      title: "Secondary Fracture Half Length " + (units.fractureHalfLength[simulationUnit] ?? ''),
      dataIndex: "SECONDARY_FRACTURE_HALF_LEN",
      key: "SECONDARY_FRACTURE_HALF_LEN",
      editable: true
    },
    {
      title: "Secondary Vertical Penetration " + (units.verticalPenetration[simulationUnit] ?? ''),
      dataIndex: "SECONDARY_VERTICAL_PENETRATION",
      key: "SECONDARY_VERTICAL_PENETRATION",
      editable: true
    },
    {
      title: "Secondary Fracture Width " + (units.fractureWidth[simulationUnit] ?? ''),
      dataIndex: "SECONDARY_FRACTURE_WIDTH",
      key: "SECONDARY_FRACTURE_WIDTH",
      editable: true
    }
  ]

  useEffect(() => {
    if (!currentStageValues?.__synced) return
    const stageValues = form.getFieldValue(['Completion', 'hydraulic_fracture'])
    const syncedStages = stageValues?.filter((stage, index) => index !== stageIndex && stage.__synced) ?? []
    syncedStages.forEach((_, index) => {
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'STAGE_LEN'], currentStageValues.STAGE_LEN)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'NUMBER_CLUSTERS'], currentStageValues.NUMBER_CLUSTERS)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SAND_USAGE'], currentStageValues.SAND_USAGE)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'FRACTURE_HALF_LEN'], currentStageValues.PRIMARY.FRACTURE_HALF_LEN)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'VERTICAL_PENETRATION'], currentStageValues.PRIMARY.VERTICAL_PENETRATION)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'FRACTURE_WIDTH'], currentStageValues.PRIMARY.FRACTURE_WIDTH)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'FRACTURE_HALF_LEN'], currentStageValues.SECONDARY.FRACTURE_HALF_LEN)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'VERTICAL_PENETRATION'], currentStageValues.SECONDARY.VERTICAL_PENETRATION)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'FRACTURE_WIDTH'], currentStageValues.SECONDARY.FRACTURE_WIDTH)
    })
  }, [currentStageValues, form, stageIndex])

  const handleTableEditorOpen = () => {
    const stageValues = form.getFieldValue(['Completion', 'hydraulic_fracture']) ?? [];
    const tableData = stageValues.map((stage, idx) => ({
      key: generateUuid(),
      stageNumber: idx + 1,
      STAGE_LEN: stage.STAGE_LEN ?? '',
      NUMBER_CLUSTERS: stage.NUMBER_CLUSTERS ?? '',
      SAND_USAGE: stage.SAND_USAGE ?? '',
      PRIMARY_FRACTURE_HALF_LEN: stage.PRIMARY?.FRACTURE_HALF_LEN ?? '',
      PRIMARY_VERTICAL_PENETRATION: stage.PRIMARY?.VERTICAL_PENETRATION ?? '',
      PRIMARY_FRACTURE_WIDTH: stage.PRIMARY?.FRACTURE_WIDTH ?? '',
      SECONDARY_FRACTURE_HALF_LEN: stage.SECONDARY?.FRACTURE_HALF_LEN ?? '',
      SECONDARY_VERTICAL_PENETRATION: stage.SECONDARY?.VERTICAL_PENETRATION ?? '',
      SECONDARY_FRACTURE_WIDTH: stage.SECONDARY?.FRACTURE_WIDTH ?? ''
    }));
    console.log('Table Data:', tableData);
    setEditingData(tableData);
    setIsTableEditorOpen(true);
  }

  const handleTableEditorCancel = () => {
    setIsTableEditorOpen(false)
  }

  const handleTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || [];
    
    currentEditingData.forEach((row, index) => {
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'STAGE_LEN'], row.STAGE_LEN);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'NUMBER_CLUSTERS'], row.NUMBER_CLUSTERS);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SAND_USAGE'], row.SAND_USAGE);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'FRACTURE_HALF_LEN'], row.PRIMARY_FRACTURE_HALF_LEN);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'VERTICAL_PENETRATION'], row.PRIMARY_VERTICAL_PENETRATION);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'FRACTURE_WIDTH'], row.PRIMARY_FRACTURE_WIDTH);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'FRACTURE_HALF_LEN'], row.SECONDARY_FRACTURE_HALF_LEN);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'VERTICAL_PENETRATION'], row.SECONDARY_VERTICAL_PENETRATION);
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'FRACTURE_WIDTH'], row.SECONDARY_FRACTURE_WIDTH);
    });

    setIsTableEditorOpen(false);
  }

  return (
    <div key={stageIndex} className={classNames('vstack gap-4', {
      'd-none': !isActive
    })}>
      {/* <div className="d-flex justify-content-end mb-3">
        <Button 
          icon={<TableOutlined />}
          onClick={handleTableEditorOpen}
          title="Open Table Editor"
        />
      </div> */}
      <FormSection
        title="Operation"
      >
        <div className="row">
          <div className="col-6">
            <div className="vstack gap-2">
              <Form.Item name={[stageIndex, 'STAGE_LEN']} label={`Stage Length ${ units.stageLength[simulationUnit] }`}>
                <InputNumber className="w-100" />
              </Form.Item>
              <Form.Item name={[stageIndex, 'NUMBER_CLUSTERS']} label="Number of clusters">
                <InputNumber className="w-100" />
              </Form.Item>
            </div>
          </div>
          <div className="col-6">
            <Form.Item name={[stageIndex, 'SAND_USAGE']} label={`Sand Usage ${ units.sandUsage[simulationUnit] }`}>
              <InputNumber className="w-100" />
            </Form.Item>
          </div >
        </div >
      </FormSection >
      <FormSection
        title="Fracture Assumption"
      >
        <div className="row">
          <div className="col-6">
            <FormSection title="Primary">
              <div className="vstack">
                <Form.Item name={[stageIndex, 'PRIMARY', 'FRACTURE_HALF_LEN']} label={`Fracture Half length ${ units.fractureHalfLength[simulationUnit] }`}>
                  <Input />
                </Form.Item>
                <Form.Item name={[stageIndex, 'PRIMARY', 'VERTICAL_PENETRATION']} label={`Vertical Penetration ${ units.verticalPenetration[simulationUnit] }`}>
                  <Input />
                </Form.Item>
                <Form.Item name={[stageIndex, 'PRIMARY', 'FRACTURE_WIDTH']} label={`Fracture width ${ units.fractureWidth[simulationUnit] }`}>
                  <Input />
                </Form.Item>
              </div>
            </FormSection>
          </div>
          <div className="col-6">
            <FormSection title="Secondary">
              <div className="vstack">
                <Form.Item name={[stageIndex, 'SECONDARY', 'FRACTURE_HALF_LEN']} label={`Fracture Half length ${ units.fractureHalfLength[simulationUnit] }`}>
                  <InputNumber className="w-100" />
                </Form.Item>
                <Form.Item name={[stageIndex, 'SECONDARY', 'VERTICAL_PENETRATION']} label={`Vertical Penetration ${ units.verticalPenetration[simulationUnit] }`}>
                  <InputNumber className="w-100" />
                </Form.Item>
                <Form.Item name={[stageIndex, 'SECONDARY', 'FRACTURE_WIDTH']} label={`Fracture width ${ units.fractureWidth[simulationUnit] }`}>
                  <InputNumber className="w-100" />
                </Form.Item>
              </div>
            </FormSection>
          </div>
        </div>
      </FormSection>
      <Modal
        title="Stage Settings Editor"
        open={isTableEditorOpen}
        onCancel={handleTableEditorCancel}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <TableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={tableColumns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </div >
  )
}

export default GenericSettings